import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  useClassTypesQuery,
  useStudentGroupsQuery,
} from "@/modules/master/presentation/api/queries";
import { usePackageTemplatesQuery } from "@/modules/packages/presentation/api/queries";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Separator,
  toast,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  CalendarIcon,
  ClipboardCopyIcon,
  GlobeIcon,
  PackageIcon,
  SparklesIcon,
  UsersIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  CAMP_EVENT_STATUS_HINTS,
  CAMP_EVENT_STATUS_LABELS,
  CAMP_EVENT_STATUS_VARIANTS,
} from "../lib/camp-status";
import {
  useCampEventQuery,
  useCampEventsQuery,
  useInitializeSummerCamp2026Mutation,
  useUpdateCampEventClassesMutation,
  useUpdateCampEventMutation,
} from "../api/queries";
import {
  resolveCampDefaultStudentGroupId,
  resolveCampPackageTemplateIds,
} from "../../domain/lib/camp-event-settings";
import {
  CampEventClassOptionsEditor,
  type EditableCampClassOption,
} from "../ui/CampEventClassOptionsEditor";
import { CampEventPackageTemplatesEditor } from "../ui/CampEventPackageTemplatesEditor";

const NO_DEFAULT_STUDENT_GROUP = "__none__";

function toEditableOptions(
  classOptions: Array<{
    classTypeId: string;
    classTypeName?: string;
    sortOrder: number;
    enabled: boolean;
    publicLabelLao: string | null;
    publicDescription: string | null;
    capacity?: number;
    waitlistEnabled?: boolean;
    waitlistCapacity?: number | null;
    lowThreshold?: number;
    criticalThreshold?: number;
    claimed?: number;
    remaining?: number;
  }>,
): EditableCampClassOption[] {
  return [...classOptions]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item) => ({
      classTypeId: item.classTypeId,
      classTypeName: item.classTypeName ?? item.classTypeId,
      sortOrder: item.sortOrder,
      enabled: item.enabled,
      publicLabelLao: item.publicLabelLao ?? "",
      publicDescription: item.publicDescription ?? "",
      capacity: item.capacity ?? 30,
      waitlistEnabled: item.waitlistEnabled ?? true,
      waitlistCapacity: item.waitlistCapacity ?? 50,
      lowThreshold: item.lowThreshold ?? 10,
      criticalThreshold: item.criticalThreshold ?? 3,
      claimed: item.claimed,
      remaining: item.remaining,
    }));
}

export function CampEventConfigPage() {
  const nav = useNavigate();
  const canUpdate = useActionPermission(["camp-events:update"]);
  const {
    data: eventsData,
    isLoading: eventsLoading,
    isError: eventsError,
    refetch: refetchEvents,
  } = useCampEventsQuery();
  const event = eventsData?.items[0];
  const initMutation = useInitializeSummerCamp2026Mutation();
  const { data: detail, isLoading: detailLoading } = useCampEventQuery(
    event?.id,
  );
  const updateMutation = useUpdateCampEventMutation();
  const updateClassesMutation = useUpdateCampEventClassesMutation();

  const classTypesQuery = useClassTypesQuery({
    limit: 200,
    offset: 0,
    sort: [{ field: "name", dir: "asc" }],
    filters: [{ field: "status", op: "eq", value: "active" }],
  });

  const packageTemplatesQuery = usePackageTemplatesQuery({
    limit: 100,
    offset: 0,
    sort: [{ field: "name", dir: "asc" }],
    filters: [{ field: "status", op: "eq", value: "active" }],
  });

  const [status, setStatus] = useState<"draft" | "open" | "closed">("draft");
  const [packageTemplateIds, setPackageTemplateIds] = useState<string[]>([]);
  const [minAge, setMinAge] = useState(5);
  const [maxAge, setMaxAge] = useState(25);
  const [ageReferenceDate, setAgeReferenceDate] = useState("2026-06-29");
  const [classOptions, setClassOptions] = useState<EditableCampClassOption[]>(
    [],
  );
  const [defaultStudentGroupId, setDefaultStudentGroupId] = useState<
    string | null
  >(null);
  const [initialized, setInitialized] = useState(false);

  const studentGroupsQuery = useStudentGroupsQuery({
    limit: 100,
    offset: 0,
    sort: [{ field: "order", dir: "asc" }],
    filters: [{ field: "status", op: "eq", value: "active" }],
  });

  useEffect(() => {
    if (!detail) return;
    setStatus(detail.status);
    setMinAge(detail.minAge);
    setMaxAge(detail.maxAge);
    setAgeReferenceDate(detail.ageReferenceDate);
    setClassOptions(toEditableOptions(detail.classOptions ?? []));
    setPackageTemplateIds(
      resolveCampPackageTemplateIds(detail.settings ?? undefined),
    );
    setDefaultStudentGroupId(
      resolveCampDefaultStudentGroupId(detail.settings ?? undefined),
    );
    setInitialized(true);
  }, [detail]);

  const availablePackageTemplates = useMemo(
    () =>
      (packageTemplatesQuery.data?.data ?? []).map((item) => ({
        id: item.id,
        name: item.name,
        totalSessions: item.totalSessions,
        validDays: item.validDays,
      })),
    [packageTemplatesQuery.data?.data],
  );

  const studentGroupOptions = useMemo(
    () => [
      { value: NO_DEFAULT_STUDENT_GROUP, label: "ບໍ່ກຳນົດກຸ່ມ (ST-xxxx)" },
      ...(studentGroupsQuery.data?.data ?? []).map((group) => ({
        value: group.id,
        label: `${group.name} (${group.code})`,
      })),
    ],
    [studentGroupsQuery.data?.data],
  );

  const availableClassTypes = useMemo(
    () =>
      (classTypesQuery.data?.data ?? []).map((item) => ({
        id: item.id,
        name: item.name,
      })),
    [classTypesQuery.data?.data],
  );

  const enabledClassCount = classOptions.filter((o) => o.enabled).length;
  const publicApiPath = event
    ? `/api/public/camp/${event.slug}/classes`
    : "";

  const handleCopyApi = async () => {
    if (!publicApiPath) return;
    try {
      await navigator.clipboard.writeText(publicApiPath);
      toast.success("ຄັດລອກ endpoint ແລ້ວ");
    } catch {
      toast.error("ຄັດລອກບໍ່ສຳເລັດ");
    }
  };

  const handleSaveAll = async () => {
    if (!event || !canUpdate) return;

    if (classOptions.length === 0) {
      toast.error("ຕ້ອງມີຢ່າງໜ້ອຍ 1 ຄລາສ");
      return;
    }

    if (minAge >= maxAge) {
      toast.error("ອາຍຸຕ່ຳສຸດຕ້ອງນ້ອຍກວ່າອາຍຸສູງສຸດ");
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: event.id,
        body: {
          status,
          minAge,
          maxAge,
          ageReferenceDate,
          settings: {
            packageTemplateIds,
            packageTemplateId: packageTemplateIds[0] ?? null,
            defaultStudentGroupId: defaultStudentGroupId || null,
          },
        },
      });
      await updateClassesMutation.mutateAsync({
        id: event.id,
        options: classOptions.map((item, index) => ({
          classTypeId: item.classTypeId,
          sortOrder: index,
          enabled: item.enabled,
          publicLabelLao: item.publicLabelLao.trim() || null,
          publicDescription: item.publicDescription.trim() || null,
          capacity: item.capacity,
          waitlistEnabled: item.waitlistEnabled,
          waitlistCapacity: item.waitlistCapacity,
          lowThreshold: item.lowThreshold,
          criticalThreshold: item.criticalThreshold,
        })),
      });
      toast.success("ບັນທຶກການຕັ້ງຄ່າສຳເລັດ");
    } catch {
      toast.error("ບັນທຶກລົ້ມເຫຼວ");
    }
  };

  const isSaving = updateMutation.isPending || updateClassesMutation.isPending;
  const isLoading = eventsLoading || detailLoading;

  const breadcrumb = (
    <Breadcrumb
      items={[
        { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
        { label: "Summer Camp 2026" },
      ]}
    />
  );

  const handleInitialize = async () => {
    try {
      const result = await initMutation.mutateAsync();
      toast.success(
        result.eventCreated
          ? "ສ້າງ Summer Camp 2026 ສຳເລັດ — ກະລຸນາກວດຄ່າ ແລະ ບັນທຶກ"
          : "ໂຫຼດຄ່າ Summer Camp 2026 ສຳເລັດ",
      );
      await refetchEvents();
    } catch {
      toast.error("ສ້າງຂໍ້ມູນ event ບໍ່ສຳເລັດ");
    }
  };

  if (eventsLoading) {
    return (
      <>
        <Header>{breadcrumb}</Header>
        <Main>
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            ກຳລັງໂຫຼດ...
          </div>
        </Main>
      </>
    );
  }

  if (eventsError) {
    return (
      <>
        <Header>{breadcrumb}</Header>
        <Main>
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle>ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ</CardTitle>
              <CardDescription>
                ກະລຸນາກວດສິດການເຂົ້າເຖິງ (camp-events:read) ຫຼື ລອງໃໝ່
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={() => refetchEvents()}>
                ລອງໃໝ່
              </Button>
            </CardContent>
          </Card>
        </Main>
      </>
    );
  }

  if (!event) {
    return (
      <>
        <Header>{breadcrumb}</Header>
        <Main>
          <div className="mx-auto max-w-2xl">
            <Card className="border-primary/20">
              <CardHeader className="text-center">
                <div className="mx-auto mb-3 flex size-14 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <SparklesIcon className="size-7" />
                </div>
                <CardTitle className="text-xl">
                  ເລີ່ມຕັ້ງຄ່າ Summer Camp 2026
                </CardTitle>
                <CardDescription className="text-base">
                  ຍັງບໍ່ມີຂໍ້ມູນ event ໃນລະບົບ — ກົດປຸ່ມດ້ານລຸ່ມເພື່ອສ້າງຄ່າເລີ່ມຕົ້ນ
                  (slug <code className="text-xs">summer-camp-2026</code>, ຄລາສ
                  5 ປະເພດ)
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-4">
                {canUpdate ? (
                  <Button
                    size="lg"
                    disabled={initMutation.isPending}
                    onClick={handleInitialize}
                    className="min-w-56"
                  >
                    {initMutation.isPending
                      ? "ກຳລັງສ້າງ..."
                      : "ເລີ່ມຕັ້ງຄ່າອັດຕໂນມັດ"}
                  </Button>
                ) : (
                  <p className="text-center text-muted-foreground text-sm">
                    ຕ້ອງມີສິດ camp-events:update ເພື່ອສ້າງ event
                  </p>
                )}
                <p className="max-w-md text-center text-muted-foreground text-xs">
                  ຫຼັງສ້າງແລ້ວ ຕັ້ງສະຖານະເປີດຮັບສະໝັກ ແລະ ບັນທຶກ — ເວັບ Summer Camp
                  ຈຶ່ງຈະຮັບໃບສະໝັກໄດ້
                </p>
              </CardContent>
            </Card>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>{breadcrumb}</Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-bold text-2xl">
                {event?.title ?? "Summer Camp 2026"}
              </h1>
              {detail ? (
                <Badge
                  variant={
                    CAMP_EVENT_STATUS_VARIANTS[detail.status] ?? "outline"
                  }
                >
                  {CAMP_EVENT_STATUS_LABELS[detail.status] ?? detail.status}
                </Badge>
              ) : null}
            </div>
            <p className="mt-1 text-muted-foreground text-sm">
              ຕັ້ງຄ່າການເປີດຮັບສະໝັກ ອາຍຸ ແລະ ຄລາສທີ່ສະແດງໃນຟອມ Summer Camp
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/camp-registrations" })}
            >
              ໃບສະໝັກ Camp
              <ArrowRightIcon className="ml-2 size-4" />
            </Button>
            {canUpdate ? (
              <Button disabled={isSaving || !initialized} onClick={handleSaveAll}>
                {isSaving ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກການຕັ້ງຄ່າ"}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <UsersIcon className="size-5 text-muted-foreground" />
                  ການຮັບສະໝັກ
                </CardTitle>
                <CardDescription>
                  ກຳນົດສະຖານະ event ແລະເງື່ອນໄຂອາຍຸສຳລັບຟອມສາທານະ
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Slug (ອ່ານເທົ່ານັ້ນ)</Label>
                    <Input value={event?.slug ?? ""} readOnly />
                  </div>

                  <div className="space-y-2 sm:col-span-2">
                    <Label>ສະຖານະການຮັບສະໝັກ</Label>
                    <SimpleSelect
                      value={status}
                      onValueChange={(v) =>
                        setStatus(v as "draft" | "open" | "closed")
                      }
                      disabled={!canUpdate}
                      className="w-full sm:max-w-xs"
                      options={[
                        { value: "draft", label: CAMP_EVENT_STATUS_LABELS.draft },
                        { value: "open", label: CAMP_EVENT_STATUS_LABELS.open },
                        {
                          value: "closed",
                          label: CAMP_EVENT_STATUS_LABELS.closed,
                        },
                      ]}
                    />
                    <p className="text-muted-foreground text-xs">
                      {CAMP_EVENT_STATUS_HINTS[status]}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="minAge">ອາຍຸຕ່ຳສຸດ</Label>
                    <Input
                      id="minAge"
                      type="number"
                      min={0}
                      value={minAge}
                      disabled={!canUpdate}
                      onChange={(e) => setMinAge(Number(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxAge">ອາຍຸສູງສຸດ</Label>
                    <Input
                      id="maxAge"
                      type="number"
                      min={1}
                      value={maxAge}
                      disabled={!canUpdate}
                      onChange={(e) => setMaxAge(Number(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2 sm:col-span-2">
                    <Label>ກຸ່ມນັກຮຽນເມື່ອ Convert</Label>
                    <SimpleSelect
                      value={defaultStudentGroupId ?? NO_DEFAULT_STUDENT_GROUP}
                      onValueChange={(value) =>
                        setDefaultStudentGroupId(
                          value === NO_DEFAULT_STUDENT_GROUP ? null : value,
                        )
                      }
                      disabled={!canUpdate || studentGroupsQuery.isLoading}
                      className="w-full sm:max-w-md"
                      options={studentGroupOptions}
                    />
                    <p className="text-muted-foreground text-xs">
                      ນັກຮຽນໃໝ່ຈາກ Camp ຈະຖືກຈັດເຂົ້າກຸ່ມນີ້ອັດຕະໂນມັດ (Member ID
                      ໃຊ້ code ຂອງກຸ່ມ)
                    </p>
                  </div>

                  <div className="space-y-2 sm:col-span-2">
                    <Label
                      htmlFor="ageReferenceDate"
                      className="flex items-center gap-2"
                    >
                      <CalendarIcon className="size-4 text-muted-foreground" />
                      ວັນອ້າງອິງຄິດໄລ່ອາຍຸ
                    </Label>
                    <Input
                      id="ageReferenceDate"
                      type="date"
                      value={ageReferenceDate}
                      disabled={!canUpdate}
                      onChange={(e) => setAgeReferenceDate(e.target.value)}
                    />
                    <p className="text-muted-foreground text-xs">
                      ໃຊ້ຄິດອາຍຸຕອນສົ່ງຟອມ — ຕ້ອງກົງກັບວັນເປີດ Camp
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <PackageIcon className="size-5 text-muted-foreground" />
                  ແພັກເກດ Camp
                </CardTitle>
                <CardDescription>
                  ຜູກໄດ້ຫຼາຍແມ່ແບບ — ຫຼັງ Convert ຖ້າມີຫຼາຍອັນຈະໃຫ້ເລືອກກ່ອນເປີດຫນ້າຂາຍ
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {packageTemplatesQuery.isLoading ? (
                  <p className="text-muted-foreground text-sm">
                    ກຳລັງໂຫຼດແມ່ແບບ...
                  </p>
                ) : availablePackageTemplates.length === 0 ? (
                  <p className="text-amber-700 text-xs dark:text-amber-400">
                    ຍັງບໍ່ມີແມ່ແບບ active — ສ້າງທີ່ ແມ່ແບບແພັກເກດ ຫຼືຮັນ{" "}
                    <code className="text-[10px]">
                      seed-summer-camp-package-template.ts
                    </code>
                  </p>
                ) : (
                  <CampEventPackageTemplatesEditor
                    selectedIds={packageTemplateIds}
                    availableTemplates={availablePackageTemplates}
                    disabled={!canUpdate || isSaving}
                    onChange={setPackageTemplateIds}
                    onViewTemplate={(templateId) =>
                      nav({
                        to: "/app/packages/templates/$id",
                        params: { id: templateId },
                      })
                    }
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">ຄລາສໃນຟອມສາທານະ</CardTitle>
                <CardDescription>
                  ເລືອກຈາກ Master → ປະເພດຄລາດສ໌, ຈັດລຳດັບ, ເປີດ/ປິດ ແລະ
                  ຕັ້ງຊື່ສະແດງຜົນ (ຖ້າຕ້ອງການ)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading || !initialized ? (
                  <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
                ) : (
                  <CampEventClassOptionsEditor
                    options={classOptions}
                    availableClassTypes={availableClassTypes}
                    disabled={!canUpdate || isSaving}
                    onChange={setClassOptions}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <GlobeIcon className="size-4 text-muted-foreground" />
                  Public API
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs uppercase">
                    ດຶງລາຍການຄລາສ
                  </p>
                  <code className="mt-1 block break-all rounded-md bg-muted px-2 py-1.5 text-xs">
                    {publicApiPath}
                  </code>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={handleCopyApi}
                >
                  <ClipboardCopyIcon className="mr-2 size-4" />
                  ຄັດລອກ endpoint
                </Button>
                <Separator />
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Summer Camp 2026 ເອີ້ນ endpoint ນີ້ຝັ່ງ server — ຕ້ອງຕັ້ງ{" "}
                  <code>CAMP_API_KEY</code> ໃນເວັບ ແລະ{" "}
                  <code>CAMP_PUBLIC_API_KEY</code> ໃນ Admin
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">ສະຫຼຸບ</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">ຄລາສເປີດສຳລັບຟອມ</span>
                  <span className="font-semibold">{enabledClassCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">ຄລາສທັງໝົດ</span>
                  <span className="font-semibold">{classOptions.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">ຊ່ວງອາຍຸ</span>
                  <span className="font-semibold">
                    {minAge}–{maxAge} ປີ
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">ແພັກເກດ Camp</span>
                  <span className="font-semibold">
                    {packageTemplateIds.length > 0
                      ? packageTemplateIds.length
                      : "—"}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Main>
    </>
  );
}
